import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Box, Loader2 } from 'lucide-react';
import { API } from '../App';

const STLViewer = ({ file, fileId, height = 400 }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const animationRef = useRef(null);
  const meshRef = useRef(null);
  const cameraRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Check if file format supports 3D preview
  const isPreviewSupported = (fileObj) => {
    if (!fileObj) return true; // No file = show default cube
    const filename = fileObj.name || fileObj;
    if (typeof filename === 'string') {
      const lower = filename.toLowerCase();
      // STL, STEP, IGES all support preview now
      return lower.endsWith('.stl') || 
             lower.endsWith('.step') || 
             lower.endsWith('.stp') ||
             lower.endsWith('.iges') ||
             lower.endsWith('.igs');
    }
    return false;
  };

  // Get file extension
  const getFileExtension = (fileObj) => {
    if (!fileObj) return null;
    const filename = fileObj.name || fileObj;
    if (typeof filename === 'string') {
      return filename.toLowerCase().split('.').pop();
    }
    return null;
  };
  
  // Check if file needs preview endpoint (STEP/IGES)
  const needsPreviewEndpoint = (fileObj) => {
    const ext = getFileExtension(fileObj);
    return ext === 'step' || ext === 'stp' || ext === 'iges' || ext === 'igs';
  };

  // Initialize scene once
  const initScene = useCallback(() => {
    if (!containerRef.current || !canvasRef.current || isInitializedRef.current) return;
    
    isInitializedRef.current = true;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      containerRef.current.clientWidth / height,
      0.1,
      1000
    );
    camera.position.set(0, 2, 5);
    cameraRef.current = camera;

    // Renderer - use the canvas ref
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvasRef.current,
      antialias: true 
    });
    renderer.setSize(containerRef.current.clientWidth, height);
    renderer.shadowMap.enabled = true;
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = false;
    controls.minDistance = 2;
    controls.maxDistance = 10;
    controlsRef.current = controls;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const spotLight = new THREE.SpotLight(0xffffff, 1);
    spotLight.position.set(10, 10, 10);
    spotLight.castShadow = true;
    scene.add(spotLight);

    const pointLight = new THREE.PointLight(0xFFFFFF, 0.5);
    pointLight.position.set(-10, -10, -10);
    scene.add(pointLight);

    // Grid helper
    const gridHelper = new THREE.GridHelper(10, 10, 0x333333, 0x222222);
    gridHelper.position.y = -1.5;
    scene.add(gridHelper);

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      if (meshRef.current) {
        meshRef.current.rotation.y += 0.005;
      }
      
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    // Add default cube
    addDefaultCube();
  }, [height]);

  const addDefaultCube = () => {
    if (!sceneRef.current) return;
    
    // Remove existing mesh
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current.geometry?.dispose();
      meshRef.current.material?.dispose();
      meshRef.current = null;
    }

    const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
    const material = new THREE.MeshStandardMaterial({
      color: 0xFFFFFF,
      metalness: 0.3,
      roughness: 0.4,
      wireframe: true
    });
    const cube = new THREE.Mesh(geometry, material);
    sceneRef.current.add(cube);
    meshRef.current = cube;
  };

  const loadSTLFile = useCallback((fileToLoad) => {
    if (!sceneRef.current) return;
    
    const loader = new STLLoader();
    
    const loadGeometry = (buffer) => {
      try {
        // Remove existing mesh
        if (meshRef.current) {
          sceneRef.current.remove(meshRef.current);
          meshRef.current.geometry?.dispose();
          meshRef.current.material?.dispose();
          meshRef.current = null;
        }

        const geometry = loader.parse(buffer);
        geometry.center();
        geometry.computeVertexNormals();
        
        // Scale to fit
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.5 / maxDim;
        geometry.scale(scale, scale, scale);
        
        // Use a visible material with good contrast
        const material = new THREE.MeshStandardMaterial({
          color: 0xFFFFFF, // White
          metalness: 0.3,
          roughness: 0.4,
          side: THREE.DoubleSide // Render both sides for better visibility
        });
        
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.position.set(0, 0, 0);
        sceneRef.current.add(mesh);
        meshRef.current = mesh;
        
        setError(null);
        setUnsupportedFormat(false);
      } catch (err) {
        console.error('Error parsing STL:', err);
        setError('Failed to load 3D model');
        addDefaultCube();
      }
    };

    // Handle ArrayBuffer directly (from preview API)
    if (fileToLoad instanceof ArrayBuffer) {
      loadGeometry(fileToLoad);
    } else if (fileToLoad instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => loadGeometry(e.target.result);
      reader.onerror = () => {
        setError('Failed to read file');
        addDefaultCube();
      };
      reader.readAsArrayBuffer(fileToLoad);
    } else if (typeof fileToLoad === 'string') {
      fetch(fileToLoad)
        .then(res => res.arrayBuffer())
        .then(loadGeometry)
        .catch(() => {
          setError('Failed to load STL file');
          addDefaultCube();
        });
    }
  }, []);

  // Initialize scene on mount
  useEffect(() => {
    initScene();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const width = containerRef.current.clientWidth;
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (meshRef.current) {
        meshRef.current.geometry?.dispose();
        meshRef.current.material?.dispose();
      }
      isInitializedRef.current = false;
    };
  }, [initScene, height]);

  // Handle file changes
  useEffect(() => {
    if (!isInitializedRef.current) return;
    
    const loadPreview = async () => {
      if (file) {
        if (isPreviewSupported(file)) {
          setUnsupportedFormat(false);
          setError(null);
          
          // Check if this is a STEP/IGES file with a fileId - fetch preview from API
          if (needsPreviewEndpoint(file) && fileId) {
            setLoading(true);
            try {
              const response = await fetch(`${API}/quote/file/${fileId}/preview`);
              if (response.ok) {
                const buffer = await response.arrayBuffer();
                loadSTLFile(buffer);
              } else {
                setError('Preview not available');
                addDefaultCube();
              }
            } catch (err) {
              console.error('Failed to load preview:', err);
              setError('Failed to load preview');
              addDefaultCube();
            }
            setLoading(false);
          } else {
            // Regular STL file - load directly
            loadSTLFile(file);
          }
        } else {
          setUnsupportedFormat(true);
          setError(null);
          addDefaultCube();
        }
      } else {
        setUnsupportedFormat(false);
        setError(null);
        addDefaultCube();
      }
    };
    
    loadPreview();
  }, [file, fileId, loadSTLFile]);

  // Show loading overlay without removing the canvas
  return (
    <div 
      ref={containerRef}
      className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden relative"
      style={{ height }}
      data-testid="stl-viewer"
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
      
      {/* Loading overlay */}
      {loading && (
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A]/90"
          data-testid="stl-viewer-loading"
        >
          <Loader2 className="w-10 h-10 text-[#CCFF00] animate-spin mb-4" />
          <p className="text-white/50 text-sm">Loading 3D preview...</p>
        </div>
      )}
      
      {/* Error overlay */}
      {error && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-[#111]/90"
          data-testid="stl-viewer-error"
        >
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
      
      {/* Unsupported format overlay */}
      {unsupportedFormat && file && (
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center bg-[#0A0A0A]/90"
          data-testid="stl-viewer-unsupported"
        >
          <div 
            className="w-24 h-24 rounded-lg flex items-center justify-center mb-4"
            style={{ backgroundColor: `${getFormatColor(file)}15` }}
          >
            <Box className="w-12 h-12" style={{ color: getFormatColor(file) }} />
          </div>
          <p className="text-white font-medium mb-1">{file.name || 'File'}</p>
          <p className="text-white/50 text-sm">.{getFileExtension(file)?.toUpperCase()} file</p>
          <p className="text-white/30 text-xs mt-2">3D preview available for STL, STEP & IGES files</p>
        </div>
      )}
      
      {/* Empty state */}
      {!file && !loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-white/30 text-sm bg-black/50 px-3 py-1 rounded">Upload files to preview</p>
        </div>
      )}
    </div>
  );
};

// Helper function to get color for file format
const getFormatColor = (fileObj) => {
  if (!fileObj) return '#CCFF00';
  const ext = typeof fileObj === 'string' ? fileObj.split('.').pop() : fileObj.name?.toLowerCase().split('.').pop();
  const formatColors = {
    'obj': '#00CCFF',
    '3mf': '#FF00CC',
    'step': '#FFCC00',
    'stp': '#FFCC00',
    'iges': '#CC00FF',
    'igs': '#CC00FF'
  };
  return formatColors[ext] || '#CCFF00';
};

export default STLViewer;
